// =====================================================================
// src/lib/v2/isBusinessActive.js
//
// Session 6c1 — the client-side derivation of "is this business currently
// active?" used by every V2 surface that needs to gate on subscription
// status (customer BusinessProfile, merchant MerchantApp/ManageBusinessTab,
// directory tiles, connect list categorization).
//
// A business is ACTIVE when EITHER:
//   (a) It has a paid activation window still in the future
//       (activated === true && activationValidTill > now), OR
//   (b) It is within its free trial window
//       (currentTime < trialStartedAt + trialDays)
//
// A business is INACTIVE when both windows have lapsed. That's the state
// where the customer sees the "temporarily inactive" banner and the Order
// button is replaced with "Notify me when active".
//
// This logic mirrors what Session 6a's ActivationModal.jsx uses internally
// for its header sub-label ('currently inactive' / 'on trial until DATE' /
// 'currently active until DATE'). Keeping them in one shared helper so
// customer-side, merchant-side, and admin-side all agree on the state.
//
// Session 6b (backend rules enforcement) will re-implement this same
// logic INSIDE firestore.rules as an isBusinessActive() function so the
// server ALSO enforces the gate — belt and suspenders. This client-side
// helper is UI gating only; a determined user could bypass it via direct
// Firestore writes until 6b lands.
// =====================================================================

// Convert a Firestore Timestamp / Date / null into a millisecond epoch,
// or null if unusable. Handles the three shapes a Firestore field can
// take when read via getBusiness() vs. onSnapshot() vs. server-side.
function toMillis(v) {
  if (v == null) return null;
  if (typeof v?.toMillis === 'function') return v.toMillis();
  if (typeof v?.seconds === 'number') return v.seconds * 1000;
  if (v instanceof Date) return v.getTime();
  if (typeof v === 'string') {
    const n = Date.parse(v);
    return Number.isNaN(n) ? null : n;
  }
  return null;
}

// ============================================================
// isBusinessActive(biz)
// ============================================================
//
// biz: a business object with any of the following fields:
//   activated:               boolean
//   activationValidTill:     Timestamp | Date | string | null
//   trialStartedAt:          Timestamp | Date | string | null
//   trialDays:               number (typically 15)
//
// Returns: boolean
//
// Trial semantics: trial is measured from trialStartedAt for trialDays
// days. If trialStartedAt is missing or trialDays is 0, the trial branch
// is skipped (business was never given a trial or trial was disabled).
// Existing businesses in prod all have trialDays: 15 from registration.
//
// Paid activation semantics: activated must be true AND activationValidTill
// must be in the future. The two are ANDed — an "activated" flag without a
// forward-dated validTill is treated as expired (safer default; happens
// after a paid window ends and admin hasn't reactivated yet).
export function isBusinessActive(biz) {
  if (!biz) return false;
  const now = Date.now();

  // Branch (a): paid activation window
  const validTillMs = toMillis(biz.activationValidTill);
  if (biz.activated && validTillMs != null && validTillMs > now) {
    return true;
  }

  // Branch (b): trial window
  const trialStartMs = toMillis(biz.trialStartedAt);
  const trialDays = typeof biz.trialDays === 'number' ? biz.trialDays : 0;
  if (trialStartMs != null && trialDays > 0) {
    const trialEndMs = trialStartMs + trialDays * 24 * 60 * 60 * 1000;
    if (trialEndMs > now) return true;
  }

  return false;
}

// ============================================================
// getSubscriptionState(biz)
// ============================================================
//
// Finer-grained derivation for UI copy. Returns one of:
//   'active-paid'  — activated + validTill in future
//   'active-trial' — in trial window (activated may or may not be set)
//   'expired'      — was activated OR had trial, but both are in the past
//   'never'        — no activation and no trial ever (fresh doc)
//
// Callers can use this to pick the right banner copy (e.g. "Trial expired
// on X" vs. "Subscription expired on Y"). isBusinessActive() alone is
// enough for the yes/no gate; this helper is for cases where we want to
// explain WHY the biz is inactive.
export function getSubscriptionState(biz) {
  if (!biz) return 'never';
  const now = Date.now();

  const validTillMs = toMillis(biz.activationValidTill);
  if (biz.activated && validTillMs != null && validTillMs > now) {
    return 'active-paid';
  }

  const trialStartMs = toMillis(biz.trialStartedAt);
  const trialDays = typeof biz.trialDays === 'number' ? biz.trialDays : 0;
  const trialEndMs = trialStartMs != null && trialDays > 0
    ? trialStartMs + trialDays * 24 * 60 * 60 * 1000
    : null;

  if (trialEndMs != null && trialEndMs > now) {
    return 'active-trial';
  }

  // At this point, biz is not active. Was it EVER active?
  if (validTillMs != null || trialEndMs != null) {
    return 'expired';
  }

  return 'never';
}
